import { AgGridReact } from "@jsq/ds/core";
import { CaseTypeDisplay } from "./CaseDetailsDrawer/types";
import { ResolutionRenderer } from "./components/ResolutionRenderer";
import { StatusRenderer } from "./components/StatusRenderer";
import { BillingCaseType } from "./types";
import type { BillingCaseRowItem, CaseResolution, CaseStatus } from "./types";

export const typeValueFormatter = (params: { value: string }): string => {
  switch (params.value) {
    case BillingCaseType.ClientInformation:
      return "Client information";
    case BillingCaseType.FirstCapitalCallDate:
      return CaseTypeDisplay.FirstCapitalCallDate;
    default:
      return "--";
  }
};

export const statusValueFormatter = (params: { value: string }): string => {
  switch (params.value) {
    case "PENDING_REVIEW":
      return "Pending review";
    case "IN_REVIEW":
      return "In review";
    case "CLOSED":
      return "Closed";
    default:
      return params.value;
  }
};

export const assigneeValueFormatter = (params: { value: string | null }): string =>
  params.value ?? "Unassigned";

export const secondaryAssigneeValueFormatter = (params: { value: string | null }): string =>
  params.value ?? "--";

export const statusCellRenderer = (params: {
  data?: BillingCaseRowItem;
}): JSX.Element | null => {
  const status = params.data?.status;
  if (!status) return null;
  return <StatusRenderer status={status} />;
};

export const resolutionCellRenderer = (params: { data?: BillingCaseRowItem }): JSX.Element => (
  <ResolutionRenderer resolution={params.data?.resolution ?? null} />
);

// Restricted to values the backend currently emits; typed against the canonical enums to catch schema drift.
export const STATUS_FILTER_VALUES: CaseStatus[] = ["IN_REVIEW", "CLOSED"];
export const RESOLUTION_FILTER_VALUES: CaseResolution[] = ["APPROVED", "INVALID"];

export const getColumnDefinitions = (
  showSecondaryAssignee: boolean,
): AgGridReact.AgGridReactProps["columnDefs"] => [
  {
    field: "billingCaseId",
    headerName: "Billing Case ID",
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
    },
    minWidth: 100,
  },
  {
    field: "type",
    headerName: "Type",
    filter: false,
    sortable: false,
    valueFormatter: typeValueFormatter,
    minWidth: 150,
  },
  {
    field: "clientName",
    headerName: "Client name",
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
    },
    minWidth: 180,
    flex: 1.5,
  },
  {
    field: "creationDate",
    headerName: "Creation date",
    filter: false,
    minWidth: 130,
  },
  {
    field: "status",
    headerName: "Status",
    filter: "agSetColumnFilter",
    filterParams: {
      values: STATUS_FILTER_VALUES,
      valueFormatter: statusValueFormatter,
    },
    cellRenderer: statusCellRenderer,
    minWidth: 150,
  },
  {
    field: "resolution",
    headerName: "Resolution",
    filter: "agSetColumnFilter",
    filterParams: {
      values: RESOLUTION_FILTER_VALUES,
    },
    cellRenderer: resolutionCellRenderer,
    minWidth: 130,
  },
  {
    field: "assignee",
    headerName: "Assignee",
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains"],
      maxNumConditions: 1,
    },
    valueFormatter: assigneeValueFormatter,
    minWidth: 130,
  },
  ...(showSecondaryAssignee
    ? [
        {
          field: "secondaryAssignee",
          headerName: "Secondary assignee",
          filter: "agTextColumnFilter",
          filterParams: {
            filterOptions: ["contains"],
            maxNumConditions: 1,
          },
          valueFormatter: secondaryAssigneeValueFormatter,
          minWidth: 130,
        },
      ]
    : []),
];
